import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Spinner,
  Text,
} from '@theme-ui/components'
import React, { useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { object, string } from 'yup'
import {
  activateUser,
  deactivateUser,
  deleteUser,
  getUsers,
  updateUser,
} from '../../api/users'
import { Modal } from '../../components'
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import Dropdown, {
  DropdownButton,
  DropdownList,
  DropdownMenu,
} from '../../components/Dropdown/Dropdown'
import { notifyErrorAPI, notifySuccess } from '../../components/Notif'
import UserForm, { UserFormValues } from './components/UserForm'

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    data: userDetail,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery(['/user/:id', id], () => {
    return getUsers(id).then((resp) => resp?.data?.data)
  })

  const mutationOptions = {
    onSuccess: () => {
      notifySuccess('User berhasil diubah')
      refetch()
    },
    onError: notifyErrorAPI,
  }

  const updateM = useMutation((data: { email; fname; lname; roleIds }) => {
    return updateUser(id, data)
  }, mutationOptions)
  const deactivateM = useMutation(() => deactivateUser(id), mutationOptions)
  const activateM = useMutation(() => activateUser(id), mutationOptions)
  const deleteM = useMutation(() => deleteUser(id), mutationOptions)

  const [action, setAction] = useState<
    'NONE' | 'DEACTIVATE' | 'ACTIVATE' | 'DELETE'
  >('NONE')

  const fullName = `${userDetail?.fname} ${userDetail?.lname}`
  const isActive = userDetail?.status === 'active'
  const isMutating =
    updateM.isLoading ||
    deactivateM.isLoading ||
    activateM.isLoading ||
    deleteM.isLoading

  function handleSubmit(values: UserFormValues) {
    updateM.mutate({
      email: values.email,
      fname: values.fname,
      lname: values.lname,
      roleIds: values.role_ids,
    })
  }

  if (isLoading && !isRefetching) {
    return (
      <Flex sx={{ justifyContent: 'center', alignItems: 'center' }} p={5}>
        <Spinner />
      </Flex>
    )
  }

  return (
    <Container px={4} py={3}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumb
          crumbs={[
            { label: 'Home', link: '/' },
            { label: 'Users', link: '/users' },
            { label: fullName, link: `/users/${id}` },
          ]}
        />
      </Box>
      <Text as="h1" mb={4}>
        {fullName}{' '}
        <Badge
          variant={
            { active: 'success', inactive: 'lightest' }[userDetail?.status]
          }
        >
          {userDetail?.status}
        </Badge>
      </Text>

      <UserForm
        initialValues={{
          email: userDetail?.email,
          role_ids: [userDetail?.roles?.[0]?.id],
          fname: userDetail?.fname,
          lname: userDetail?.lname,
        }}
        onSubmit={handleSubmit}
        renderAction={({ submitForm }) => {
          function handleConfirmAction() {
            if (action === 'ACTIVATE') {
              submitForm().then(() =>
                activateM.mutateAsync().then(() => setAction('NONE'))
              )
            } else if (action === 'DEACTIVATE') {
              submitForm().then(() =>
                deactivateM.mutateAsync().then(() => setAction('NONE'))
              )
            } else if (action === 'DELETE') {
              deleteM.mutateAsync().then(() => {
                setAction('NONE')
                navigate('/users')
              })
            }
          }

          return (
            <>
              <Dropdown placement="top-end">
                <DropdownButton variant="secondary">Tindakan</DropdownButton>

                <DropdownList>
                  <DropdownMenu
                    color="error"
                    onClick={() => setAction('DELETE')}
                  >
                    Hapus
                  </DropdownMenu>
                  <DropdownMenu
                    onClick={() =>
                      setAction(isActive ? 'DEACTIVATE' : 'ACTIVATE')
                    }
                  >
                    {isActive ? 'Non-Aktifkan' : 'Aktifkan'}
                  </DropdownMenu>
                </DropdownList>
              </Dropdown>

              <Modal
                isOpen={action !== 'NONE'}
                onRequestClose={() => setAction('NONE')}
                header={`Anda yakin untuk ${
                  {
                    ACTIVATE: 'mengaktifkan?',
                    DEACTIVATE: 'menonaktifkan?',
                    DELETE: 'menghapus?',
                  }[action]
                }`}
              >
                <Box sx={{ textAlign: 'right' }}>
                  <Button
                    type="button"
                    disabled={isMutating}
                    onClick={() => setAction('NONE')}
                    px={4}
                  >
                    Tidak
                  </Button>
                  <Button
                    type="button"
                    disabled={isMutating}
                    onClick={handleConfirmAction}
                    ml={3}
                    px={4}
                  >
                    Ya
                  </Button>
                </Box>
              </Modal>
            </>
          )
        }}
      />
    </Container>
  )
}

const validationSchema = object({
  email: string().email().required(),
})
